import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";

const BASE_URL = "https://1woodfloors.com";

interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface PageBreadcrumbProps {
  items: BreadcrumbItem[];
  /** "contained" sits in a max-w container; "overlay" floats over a dark hero */
  variant?: "contained" | "overlay";
}

const PageBreadcrumb = ({ items, variant = "contained" }: PageBreadcrumbProps) => {
  const isOverlay = variant === "overlay";
  const location = useLocation();

  // Inject BreadcrumbList JSON-LD structured data
  useEffect(() => {
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, i) => {
        const isLast = i === items.length - 1;
        // Resolve the URL: use item.to if available, otherwise current page path
        const path = item.to || location.pathname;
        // Clean anchor-only paths like /#products → /
        const cleanPath = path.startsWith("/#") ? "/" : path;
        return {
          "@type": "ListItem",
          "position": i + 1,
          "name": item.label,
          ...(isLast
            ? {}
            : { "item": `${BASE_URL}${cleanPath}` }),
        };
      }),
    };

    let scriptEl = document.getElementById("breadcrumb-jsonld") as HTMLScriptElement | null;
    if (!scriptEl) {
      scriptEl = document.createElement("script");
      scriptEl.id = "breadcrumb-jsonld";
      scriptEl.type = "application/ld+json";
      document.head.appendChild(scriptEl);
    }
    scriptEl.textContent = JSON.stringify(jsonLd);

    return () => {
      scriptEl?.remove();
    };
  }, [items, location.pathname]);

  return (
    <nav
      aria-label="Breadcrumb"
      className={
        isOverlay
          ? "absolute top-0 left-0 right-0 z-30 px-5 md:px-10 lg:px-16 pt-3"
          : "mb-10"
      }
    >
      <ol className="flex items-center gap-2 text-[11px] list-none p-0 m-0">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          const colorClass = isOverlay
            ? isLast
              ? "text-white/70 font-medium"
              : "text-white/50 hover:text-[#1B4332] transition-colors duration-200"
            : isLast
              ? "text-[#18181b] font-medium"
              : "text-[#a1a1aa] hover:text-[#18181b] transition-colors";

          return (
            <li key={i} className="flex items-center gap-2">
              {i > 0 && (
                <span className={isOverlay ? "text-white/25" : "text-[#a1a1aa]"}>/</span>
              )}
              {item.to && !isLast ? (
                <Link to={item.to} className={colorClass}>
                  {item.label}
                </Link>
              ) : (
                <span className={colorClass}>{item.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default PageBreadcrumb;
